import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { MfaControllerApi } from '../../openapi/keymaker';
import ErrorService from '../../services/ErrorService';
import { signOutUser } from '../../slices/AuthSlice';
import {
  showErrorToast,
  showSuccessToast,
} from '../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../slices/store';
import { ErrorCode } from '../../types';
import { getKeymakerConfiguration } from '../../utils/OpenapiConfigurationUtils';
import Button from '../Button';
import SidebarModalForm from '../Modals/SidebarModalForm';
import ControlledPhoneNumberInput from '../inputs/ControlledPhoneNumberInput';

interface FormData {
  phoneNumber: string;
}

interface SMSSetupFormProps {
  isOpen: boolean;
  onClose(): void;
  setVerificationPhoneNumber(value: string): void;
}

const SMSSetupForm: React.FC<SMSSetupFormProps> = ({
  isOpen,
  onClose,
  setVerificationPhoneNumber,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (values: FormData) => {
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).sendMfaSms(
        values.phoneNumber,
      );
      setVerificationPhoneNumber(values.phoneNumber);
      onClose();
      dispatch(
        showSuccessToast(
          'Successfully sent authentication code to phone number.',
        ),
      );
    } catch (e: any) {
      const errorCode = ErrorService.getErrorCode(e);
      if (
        errorCode === ErrorCode.UNAUTHORIZED ||
        errorCode === ErrorCode.FORBIDDEN
      ) {
        await dispatch(signOutUser());
        dispatch(
          showErrorToast('Your session has expired. Please log back in.'),
        );
        navigate('/login');
        return;
      }
      ErrorService.notify(
        'Unable to send authentication code to phone number',
        e,
        {
          data: { phoneNumber: values.phoneNumber },
        },
      );
      dispatch(
        showErrorToast(
          'We had a problem sending authentication code to phone number',
          'Please try again in a few moments.',
        ),
      );
    }
  };

  return (
    <SidebarModalForm
      isOpen={isOpen}
      onClose={onClose}
      isSubmitting={isSubmitting}
      title='Two-Factor Authentication: Text Messages (SMS)'
      subtitle='Receive verification codes via text messages'
      onSubmit={handleSubmit(onSubmit)}
      actionTitle='Send Confirmation Code'
      cancelTitle='Back'
      hideFooterActions
    >
      <div>
        <p className='font-inter font-normal text-zen-dark-9'>
          Enter your mobile phone number.
        </p>
        <p className='font-inter font-normal text-zen-dark-9 text-sm mt-2'>
          We’ll send you a text message with a confirmation code to this number
        </p>
        <div className='mt-5'>
          <ControlledPhoneNumberInput<FormData, 'phoneNumber'>
            control={control}
            name='phoneNumber'
            placeholder='+1 (702) 123-4567'
            rules={{
              required: 'Please enter your phone number',
            }}
          />
        </div>
      </div>
      <div className='p-2 md:p-4 bg-white border-t border-gray-200 bottom-0 space-x-5 flex flex-row justify-start items-center left-0 right-0 px-3 absolute py-2 w-full'>
        <Button
          onPress={onClose}
          label='Back'
          variant='primary-outline'
          buttonSize='sm'
          fullWidth
        />
        <Button
          isSubmitting={isSubmitting}
          type='submit'
          label='Send Confirmation Code'
          onPress={handleSubmit(onSubmit)}
          buttonSize='sm'
          fullWidth
        />
      </div>
    </SidebarModalForm>
  );
};

export default SMSSetupForm;
